import * as React from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Input,
  LinearProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { cloudinaryUrl } from '@realadvisor/cloudinary';
import { type UseComboboxReturnValue, useCombobox } from 'downshift';
import { useNavigate } from 'react-router-dom';
import { useSystem } from 'react-system';

import { gql } from '../../../apollo/__generated__';
import type {
  SearchLeadsTsvQuery,
  SearchLotsQuery,
  SearchTeamsQuery,
  SearchUsersQuery,
} from '../../../apollo/__generated__/graphql';
import { SEARCH_LEADS_TSV } from '../../../apollo/components/LeadsSelect';
import { SEARCH_LOTS } from '../../../apollo/components/LotsSelect';
import { TeamAvatar } from '../../../apollo/components/TeamAvatar';
import { SEARCH_USERS } from '../../../apollo/components/user-select/UserSelect';
import { UserAvatar as ApolloUserAvatar } from '../../../apollo/components/UserAvatar';
import { useAppData } from '../../../apollo/providers/AppDataProvider';
import { formatAddress } from '../../../apollo/utils/formatting';
import { userCanViewLeadDetails } from '../../../apollo/utils/listingPermissions';
import { toGlobalId } from '../../../shared/global-id';
import { Portal } from '../../controls/popup';
import { useDebouncedHandler } from '../../hooks/debounce';
import { useLocale } from '../../hooks/locale';
import { useTheme } from '../../hooks/theme';
import { Close } from '../../icons/close';
import { GpsFixed } from '../../icons/gps-fixed';
import { Home } from '../../icons/home';
import { People } from '../../icons/people';
import { Person } from '../../icons/person';
import { Search } from '../../icons/search';

const SEARCH_TEAMS = gql(/* GraphQL */ `
  query SearchTeams($q: String!) {
    search_teams_tsv(args: { search_text: $q }, limit: 10) {
      id
      name
      logo {
        id
        url
      }
      team_images(limit: 1) {
        image {
          url
        }
      }
      emails(order_by: { primary: desc, verified: desc }, limit: 1) {
        email
      }
    }
  }
`);

type Props = {
  onClose: () => void;
  menuOpening: boolean;
};

type UsersNode = SearchUsersQuery['search_users_tsv'][number];
type LeadsNode = SearchLeadsTsvQuery['search_leads_tsv'][number];
type LotsNode = SearchLotsQuery['search_lots_tsv'][number];
type TeamsNode = SearchTeamsQuery['search_teams_tsv'][number];

// Add a union type for all possible node types
type SearchNode = UsersNode | LeadsNode | LotsNode | TeamsNode;

// Add a type for nodes with __typename
type TypedNode = SearchNode & { __typename: string };

// Type guards for each node type
const isUserNode = (
  node: TypedNode,
): node is UsersNode & { __typename: 'User' } => node.__typename === 'User';

const isLeadNode = (
  node: TypedNode,
): node is LeadsNode & { __typename: 'Lead' } => node.__typename === 'Lead';

const isLotNode = (node: TypedNode): node is LotsNode & { __typename: 'Lot' } =>
  node.__typename === 'Lot';

const isTeamNode = (
  node: TypedNode,
): node is TeamsNode & { __typename: 'Team' } => node.__typename === 'Team';

// Memoized components
const SearchListItem = React.memo(
  ({
    item,
    index,
    combobox,
    icon,
    label,
    chipLabel,
    secondaryText,
  }: {
    item: TypedNode;
    index: number;
    combobox: UseComboboxReturnValue<TypedNode>;
    icon: React.ReactNode;
    label: string;
    chipLabel: string;
    secondaryText: React.ReactNode;
  }) => {
    const { highlightedIndex, getItemProps } = combobox;
    const { colors } = useTheme();

    return (
      <ListItemButton
        {...getItemProps({
          item,
          index,
          selected: index === highlightedIndex,
        })}
        sx={{
          py: 0.75,
          '&.Mui-selected': {
            backgroundColor: colors.grey200,
          },
        }}
      >
        <ListItemAvatar
          sx={{
            minWidth: 48,
            paddingRight: 1,
            '& .MuiAvatar-root': { width: 48, height: 48 },
          }}
        >
          {icon}
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {label}
              </Typography>
              <Chip
                size="small"
                label={chipLabel}
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  backgroundColor: colors.grey300,
                  color: colors.grey800,
                }}
              />
            </Box>
          }
          secondary={
            <Typography
              variant="caption"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              {secondaryText}
            </Typography>
          }
        />
      </ListItemButton>
    );
  },
);

const UserListItem = React.memo(
  ({
    user,
    index,
    combobox,
  }: {
    user: UsersNode & { __typename: 'User' };
    index: number;
    combobox: UseComboboxReturnValue<UsersNode>;
  }) => {
    const emails = user.primaryEmail.map(email => email.email);
    const email = emails[0] ?? '';

    return (
      <SearchListItem
        item={user}
        index={index}
        combobox={combobox as UseComboboxReturnValue<TypedNode>}
        icon={<ApolloUserAvatar user={user} size={48} />}
        label={[user.first_name, ' ', user.last_name].join('')}
        chipLabel="User"
        secondaryText={email}
      />
    );
  },
);

const LeadListItem = React.memo(
  ({
    lead,
    index,
    combobox,
  }: {
    lead: LeadsNode & { __typename: 'Lead' };
    index: number;
    combobox: UseComboboxReturnValue<LeadsNode>;
  }) => {
    const { me } = useAppData();
    const canViewLeadDetails = userCanViewLeadDetails(me, lead);

    const user = lead.contact;
    const primary = !canViewLeadDetails
      ? lead.anonymized_contact
      : user
      ? [user.first_name, ' ', user.last_name].join('')
      : null;
    const { colors } = useTheme();

    return (
      <SearchListItem
        item={lead}
        index={index}
        combobox={combobox as UseComboboxReturnValue<TypedNode>}
        icon={
          <Avatar sx={{ bgcolor: colors.primaryMain, width: 48, height: 48 }}>
            <GpsFixed size={24} />
          </Avatar>
        }
        label={primary ?? 'Anonymous User'}
        chipLabel="Lead"
        secondaryText={formatAddress(
          {
            street_number: lead.property?.street_number,
            route: lead.property?.route,
            postcode: lead.property?.postcode,
            locality: lead.property?.locality,
          },
          ', ',
          true,
          { hideRoute: !canViewLeadDetails },
        )}
      />
    );
  },
);

const LotListItem = React.memo(
  ({
    lot,
    index,
    combobox,
  }: {
    lot: LotsNode & { __typename: 'Lot' };
    index: number;
    combobox: UseComboboxReturnValue<LotsNode>;
  }) => {
    const { colors } = useTheme();
    const image =
      lot.property?.property_images?.[0]?.image?.url &&
      cloudinaryUrl(
        { w: 96, h: 96, c: 'fill', f: 'jpg', q: 90 },
        lot.property?.property_images?.[0]?.image?.url,
      );
    const brokerName = lot.broker ? lot.broker.full_name : '';
    const formattedAddress = formatAddress(
      {
        street_number: lot.property?.street_number,
        route: lot.property?.route,
        postcode: lot.property?.postcode,
        locality: lot.property?.locality,
      },
      ', ',
    );

    return (
      <SearchListItem
        item={lot}
        index={index}
        combobox={combobox as UseComboboxReturnValue<TypedNode>}
        icon={
          <Avatar
            variant="square"
            src={image ?? undefined}
            sx={{
              width: 48,
              height: 48,
              bgcolor: image ? colors.grey100 : colors.primaryMain,
              borderRadius: 1,
            }}
          >
            <Home size={24} />
          </Avatar>
        }
        label={lot.title ? lot.title : formattedAddress}
        chipLabel="Listing"
        secondaryText={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
            {lot.title && <span>{formattedAddress}</span>}
            {brokerName && (
              <span style={{ color: colors.grey600, fontSize: '0.7rem' }}>
                {brokerName}
              </span>
            )}
          </Box>
        }
      />
    );
  },
);

const TeamListItem = React.memo(
  ({
    item,
    index,
    combobox,
  }: {
    item: TeamsNode & { __typename: 'Team' };
    index: number;
    combobox: UseComboboxReturnValue<TeamsNode>;
  }) => {
    return (
      <SearchListItem
        item={item}
        index={index}
        combobox={combobox as UseComboboxReturnValue<TypedNode>}
        icon={
          <TeamAvatar
            logoUrl={item.logo?.url ?? item.team_images?.[0]?.image?.url}
            size={48}
          />
        }
        label={item.name}
        chipLabel="Team"
        secondaryText={item.emails[0]?.email ?? ''}
      />
    );
  },
);

const Popup = React.memo(
  ({
    popupRef,
    open,
    children,
    menuOpening,
  }: {
    popupRef: { current: null | HTMLDivElement };
    open: boolean;
    children: React.ReactNode;
    menuOpening: boolean;
  }) => {
    const { media } = useSystem();
    const { depth, borderRadius } = useTheme();

    // Prevent background scroll when popup is open
    React.useEffect(() => {
      if (open) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [open]);

    return (
      <Portal>
        <Paper
          ref={popupRef}
          square={true}
          style={{ display: open ? 'flex' : 'none' }}
          css={media({
            position: ['fixed', 'fixed', 'absolute'],
            zIndex: depth.inputPopup,
            left: [
              '16px',
              (menuOpening ? 238 : 0) + 16,
              (menuOpening ? 238 : 58) + 16,
            ],
            right: ['16px', '16px', '16px'],
            top: 58,
            bottom: ['16px', 'auto', 'auto'],
            maxHeight: 'calc(100vh - 80px)',
            margin: ['0', '0 auto', '0'],
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: borderRadius.medium,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s',
          })}
        >
          {children}
        </Paper>
      </Portal>
    );
  },
);

const useSearchData = () => {
  const [searchTeams, { data: teams, loading: loadingTeams }] =
    useLazyQuery(SEARCH_TEAMS);

  const [searchUsers, { data: users, loading: loadingUsers }] =
    useLazyQuery<SearchUsersQuery>(SEARCH_USERS);

  const [searchLeads, { data: leads, loading: loadingLeads }] =
    useLazyQuery(SEARCH_LEADS_TSV);

  const [searchLots, { data: lots, loading: loadingLots }] =
    useLazyQuery<SearchLotsQuery>(SEARCH_LOTS);

  // Store previous results to preserve them during refetching
  const [prevUsers, setPrevUsers] = React.useState<
    (UsersNode & { __typename: 'User' })[]
  >([]);
  const [prevLeads, setPrevLeads] = React.useState<
    (LeadsNode & { __typename: 'Lead' })[]
  >([]);
  const [prevLots, setPrevLots] = React.useState<
    (LotsNode & { __typename: 'Lot' })[]
  >([]);
  const [prevTeams, setPrevTeams] = React.useState<
    (TeamsNode & { __typename: 'Team' })[]
  >([]);

  // Update previous results when new data arrives
  React.useEffect(() => {
    if (users?.search_users_tsv) {
      setPrevUsers(
        users.search_users_tsv.map(user => ({
          ...user,
          __typename: 'User',
        })),
      );
    }
  }, [users]);

  React.useEffect(() => {
    if (leads?.search_leads_tsv) {
      setPrevLeads(
        leads.search_leads_tsv.map(lead => ({
          ...lead,
          __typename: 'Lead',
        })),
      );
    }
  }, [leads]);

  React.useEffect(() => {
    if (lots?.search_lots_tsv) {
      setPrevLots(
        lots.search_lots_tsv.map(lot => ({
          ...lot,
          __typename: 'Lot',
        })),
      );
    }
  }, [lots]);

  React.useEffect(() => {
    if (teams?.search_teams_tsv) {
      setPrevTeams(
        teams.search_teams_tsv.map(team => ({
          ...team,
          __typename: 'Team',
        })),
      );
    }
  }, [teams]);

  const searchData = React.useMemo(
    () => ({
      users: prevUsers,
      leads: prevLeads,
      lots: prevLots,
      teams: prevTeams,
      loading: loadingUsers || loadingLeads || loadingLots || loadingTeams,
      loadingUsers,
      loadingLeads,
      loadingLots,
      loadingTeams,
    }),
    [
      prevUsers,
      prevLeads,
      prevLots,
      prevTeams,
      loadingUsers,
      loadingLeads,
      loadingLots,
      loadingTeams,
    ],
  );

  const refetch = React.useCallback(
    (search: string) => {
      searchUsers({
        variables: {
          q: search,
        },
      });
      searchLeads({
        variables: {
          where: {},
          q: search,
        },
      });
      searchLots({
        variables: {
          q: search,
        },
      });
      searchTeams({
        variables: {
          q: search,
        },
      });
    },
    [searchUsers, searchLeads, searchLots, searchTeams],
  );

  return { ...searchData, refetch };
};

const ApiBasedGlobalSearch = ({ onClose, menuOpening }: Props) => {
  const {
    users,
    teams,
    leads,
    lots,
    loading,
    loadingUsers,
    loadingLeads,
    loadingLots,
    loadingTeams,
    refetch,
  } = useSearchData();
  const [activeTab, setActiveTab] = React.useState(0);
  const { media } = useSystem();
  const navigate = useNavigate();
  const { t } = useLocale();
  const { colors, borderRadius } = useTheme();
  const isMetaOrCtrlKeyPressed = React.useRef<boolean>(false);

  // Create a debounced search handler
  const debouncedSearch = useDebouncedHandler(300, (term: string) => {
    if (term.length > 1) {
      refetch(term);
    }
  });

  const suggestions = React.useMemo(
    () => [...users, ...leads, ...lots, ...teams],
    [users, leads, lots, teams],
  );

  const filteredResults = React.useMemo(() => {
    if (activeTab === 0) {
      return suggestions;
    } else if (activeTab === 1) {
      return suggestions.filter(isUserNode);
    } else if (activeTab === 2) {
      return suggestions.filter(isLeadNode);
    } else if (activeTab === 3) {
      return suggestions.filter(isLotNode);
    } else if (activeTab === 4) {
      return suggestions.filter(isTeamNode);
    }
    return [];
  }, [activeTab, suggestions]);

  const indices = React.useMemo(() => {
    const map = new Map();
    filteredResults.forEach((suggestion, index) => {
      map.set(suggestion, index);
    });
    return map;
  }, [filteredResults]);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);

  // Handle click outside to close popup
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popupRef.current &&
        !popupRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const combobox = useCombobox({
    items: filteredResults,
    itemToString: () => '',
    onInputValueChange: ({ inputValue }) => debouncedSearch(inputValue ?? ''),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        const getUrl = (item: (typeof suggestions)[number]) => {
          switch (item.__typename) {
            case 'User':
              return `/v1/users/${toGlobalId('User', item.id)}`;
            case 'Lead':
              return `/leads/${toGlobalId('Lead', item.id)}`;
            case 'Lot':
              return `/listings/${item.id}`;
            case 'Team': {
              return `/teams/${item.id}`;
            }
            default:
              return '';
          }
        };

        const url = getUrl(selectedItem);

        // Check if Cmd/Ctrl key is pressed using the ref
        if (isMetaOrCtrlKeyPressed.current === true) {
          // Open in new tab and keep the popup open
          window.open(url, '_blank');
        } else {
          // Navigate in current tab and close the popup
          onClose();
          navigate(url);
        }
      }
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      if (
        isMetaOrCtrlKeyPressed.current === true &&
        (type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
          type === useCombobox.stateChangeTypes.ItemClick)
      ) {
        return {
          ...changes,
          isOpen: true,
          inputValue: state.inputValue,
        };
      }

      return changes;
    },
  });

  const { getComboboxProps, getInputProps, getMenuProps, inputValue } =
    combobox;

  const handleTabChange = React.useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleInputBlur = React.useCallback(() => {
    if (inputValue.length <= 1) {
      onClose();
    }
  }, [inputValue, onClose]);

  // Render tab icon with loading indicator
  const renderTabIcon = (
    Icon: React.ComponentType<{ size: number }>,
    isLoading: boolean,
  ) => {
    return (
      <Box sx={{ position: 'relative' }}>
        <Icon size={16} />
        {isLoading && (
          <LinearProgress
            variant="indeterminate"
            sx={{
              position: 'absolute',
              bottom: -4,
              left: 0,
              right: 0,
              height: 2,
              borderRadius: 1,
              backgroundColor: 'transparent',
              opacity: 0.7,
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box
      px={2}
      width={1}
      {...getComboboxProps({
        onKeyUp: () => {
          isMetaOrCtrlKeyPressed.current = false;
        },
        onKeyDown: e => {
          if (e.metaKey || e.ctrlKey) {
            isMetaOrCtrlKeyPressed.current = true;
          } else {
            isMetaOrCtrlKeyPressed.current = false;
          }
        },
      })}
    >
      <Input
        {...getInputProps({
          onBlur: handleInputBlur,
        })}
        css={media({
          backgroundColor: colors.grey200,
          borderRadius: borderRadius.small,
          width: [
            menuOpening ? 'calc(100vw - 238px - 32px)' : '100%',
            menuOpening ? 'calc(100vw - 238px - 32px)' : '100%',
            menuOpening
              ? 'calc(100vw - 238px - 32px)'
              : 'calc(100vw - 58px - 32px)',
          ],
        })}
        inputRef={inputRef}
        autoFocus={true}
        placeholder={t('search')}
        fullWidth={true}
        disableUnderline={true}
        startAdornment={
          <IconButton onClick={handleClose}>
            {loading ? (
              <Box sx={{ position: 'relative', width: 24, height: 24 }}>
                <CircularProgress
                  size={24}
                  thickness={3}
                  disableShrink
                  sx={{
                    color: colors.primaryLight,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                />
                <Search
                  size={16}
                  css={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    opacity: 1,
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ position: 'relative', width: 24, height: 24 }}>
                <Search
                  size={16}
                  css={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    opacity: 1,
                  }}
                />
              </Box>
            )}
          </IconButton>
        }
        endAdornment={
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        }
      />

      <Popup
        popupRef={popupRef}
        menuOpening={menuOpening}
        open={suggestions.length > 0 && inputValue.length > 1}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            TabIndicatorProps={{
              sx: { height: 2 },
            }}
            sx={{
              borderBottom: `1px solid ${colors.grey300}`,
              minHeight: { xs: 48 },
              '& .MuiTab-root': {
                minWidth: 'auto',
                minHeight: { xs: 48 },
                px: { xs: 2, sm: 2.5 },
                fontSize: '0.875rem',
                textTransform: 'none',
                fontWeight: 500,
                color: colors.grey600,
                '&.Mui-selected': {
                  color: colors.primaryMain,
                },
              },
            }}
          >
            <Tab
              icon={renderTabIcon(Search, loading)}
              iconPosition="start"
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {t('All')}
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey500,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    {suggestions.length}
                  </Typography>
                </Box>
              }
            />
            <Tab
              icon={renderTabIcon(Person, loadingUsers)}
              iconPosition="start"
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {t('users')}
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey500,
                    }}
                  >
                    {users.length}
                  </Typography>
                </Box>
              }
            />
            <Tab
              icon={renderTabIcon(GpsFixed, loadingLeads)}
              iconPosition="start"
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {t('Leads')}
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey500,
                    }}
                  >
                    {leads.length}
                  </Typography>
                </Box>
              }
            />
            <Tab
              icon={renderTabIcon(Home, loadingLots)}
              iconPosition="start"
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {t('listings')}
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey500,
                    }}
                  >
                    {lots.length}
                  </Typography>
                </Box>
              }
            />
            <Tab
              icon={renderTabIcon(People, loadingTeams)}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('teams')}
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey500,
                    }}
                  >
                    {teams.length}
                  </Typography>
                </Box>
              }
            />
          </Tabs>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              maxHeight: 'calc(100% - 48px)',
            }}
          >
            <List
              {...getMenuProps()}
              sx={{
                width: '100%',
                p: 0,
                '& .MuiListItemButton-root': {
                  py: { xs: 1.5, sm: 1 },
                },
                '& .MuiListItemAvatar-root': {
                  minWidth: { xs: '56px', sm: '48px' },
                },
                '& .MuiAvatar-root': {
                  width: { xs: 48, sm: 40 },
                  height: { xs: 48, sm: 40 },
                },
                '& .MuiListItemText-primary': {
                  fontSize: { xs: '1rem', sm: '0.875rem' },
                },
                '& .MuiListItemText-secondary': {
                  fontSize: { xs: '0.875rem', sm: '0.75rem' },
                },
                '& .MuiChip-root': {
                  height: { xs: 20, sm: 18 },
                  fontSize: { xs: '0.75rem', sm: '0.65rem' },
                },
                '& .MuiListSubheader-root': {
                  py: { xs: 1, sm: 0.75 },
                  px: { xs: 2.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '0.75rem' },
                  fontWeight: 600,
                  backgroundColor: colors.grey100,
                  lineHeight: '24px',
                },
                '& .MuiDivider-root': {
                  my: { xs: 0.75, sm: 0.5 },
                },
              }}
            >
              {filteredResults.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {t('No results found')}
                  </Typography>
                </Box>
              ) : (
                <>
                  {activeTab === 0 && (
                    <>
                      {users.length > 0 && (
                        <>
                          <ListSubheader
                            disableSticky={true}
                            sx={{ backgroundColor: colors.grey100 }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Person size={16} />
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                              >
                                {t('users')}
                              </Typography>
                            </Box>
                          </ListSubheader>
                          {users.map(user => (
                            <UserListItem
                              key={user.id}
                              user={user as UsersNode & { __typename: 'User' }}
                              index={indices.get(user)}
                              combobox={
                                combobox as UseComboboxReturnValue<UsersNode>
                              }
                            />
                          ))}
                        </>
                      )}

                      {leads.length > 0 && (
                        <>
                          <ListSubheader
                            disableSticky={true}
                            sx={{ backgroundColor: colors.grey100 }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <GpsFixed size={16} />
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                              >
                                {t('Leads')}
                              </Typography>
                            </Box>
                          </ListSubheader>
                          {leads.map(lead => (
                            <LeadListItem
                              key={lead.id}
                              lead={lead as LeadsNode & { __typename: 'Lead' }}
                              index={indices.get(lead)}
                              combobox={
                                combobox as UseComboboxReturnValue<LeadsNode>
                              }
                            />
                          ))}
                        </>
                      )}

                      {lots.length > 0 && (
                        <>
                          <ListSubheader
                            disableSticky={true}
                            sx={{ backgroundColor: colors.grey100 }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Home size={16} />
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                              >
                                {t('listings')}
                              </Typography>
                            </Box>
                          </ListSubheader>
                          {lots.map(lot => (
                            <LotListItem
                              key={lot.id}
                              lot={lot as LotsNode & { __typename: 'Lot' }}
                              index={indices.get(lot)}
                              combobox={
                                combobox as UseComboboxReturnValue<LotsNode>
                              }
                            />
                          ))}
                        </>
                      )}

                      {teams.length > 0 && (
                        <>
                          <ListSubheader
                            disableSticky={true}
                            sx={{ backgroundColor: colors.grey100 }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <People size={16} />
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                              >
                                {t('teams')}
                              </Typography>
                            </Box>
                          </ListSubheader>
                          {teams.map(team => (
                            <TeamListItem
                              key={team.id}
                              item={team as TeamsNode & { __typename: 'Team' }}
                              index={indices.get(team)}
                              combobox={
                                combobox as UseComboboxReturnValue<TeamsNode>
                              }
                            />
                          ))}
                        </>
                      )}
                    </>
                  )}

                  {activeTab === 1 && (
                    <>
                      {users.map(user => (
                        <UserListItem
                          key={user.id}
                          user={user as UsersNode & { __typename: 'User' }}
                          index={indices.get(user)}
                          combobox={
                            combobox as UseComboboxReturnValue<UsersNode>
                          }
                        />
                      ))}
                    </>
                  )}

                  {activeTab === 2 && (
                    <>
                      {leads.map(lead => (
                        <LeadListItem
                          key={lead.id}
                          lead={lead as LeadsNode & { __typename: 'Lead' }}
                          index={indices.get(lead)}
                          combobox={
                            combobox as UseComboboxReturnValue<LeadsNode>
                          }
                        />
                      ))}
                    </>
                  )}

                  {activeTab === 3 && (
                    <>
                      {lots.map(lot => (
                        <LotListItem
                          key={lot.id}
                          lot={lot as LotsNode & { __typename: 'Lot' }}
                          index={indices.get(lot)}
                          combobox={
                            combobox as UseComboboxReturnValue<LotsNode>
                          }
                        />
                      ))}
                    </>
                  )}

                  {activeTab === 4 && (
                    <>
                      {teams.map(team => (
                        <TeamListItem
                          key={team.id}
                          item={team as TeamsNode & { __typename: 'Team' }}
                          index={indices.get(team)}
                          combobox={
                            combobox as UseComboboxReturnValue<TeamsNode>
                          }
                        />
                      ))}
                    </>
                  )}
                </>
              )}
            </List>
          </Box>
        </Box>
      </Popup>
    </Box>
  );
};

export const GlobalSearch = React.memo(({ onClose, menuOpening }: Props) => (
  <ApiBasedGlobalSearch onClose={onClose} menuOpening={menuOpening} />
));
